import React, { useState } from "react";
import {
  BookOpen, Laugh, Flame, ShoppingBag,
  Hash, Megaphone, Quote, Pencil,
  Radar, Music2, Wand2, HelpCircle,
} from "lucide-react";
import CopyButton from "./CopyButton";
import EditableText from "./EditableText";
import ImproveDialog from "./ImproveDialog";
import { Button } from "./ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";
import { toast } from "sonner";

// Maps the anchor_source enum (returned by the model) to a human label +
// a one-line explanation of WHY that channel feeds the hook the way it
// does. Used by the "Why this hook?" tooltip on each PostCard.
const ANCHOR_SOURCE_META = {
  reddit:               { label: "Reddit",         note: "Pain-point thread → contrarian reframe" },
  google_trends:        { label: "Google Trends",  note: "Search intent surge → observational reframe" },
  youtube_autocomplete: { label: "YouTube",        note: "Autocomplete query → insight reveal" },
  tiktok:               { label: "TikTok",         note: "Aspirational moment → POV / immersive" },
  hackernews:           { label: "Hacker News",    note: "Tech-curiosity signal → insight reveal" },
};

const HOOK_STYLE_NOTE = {
  contrarian:  "Punctures the audience's default belief.",
  pov:         "Drops the viewer into the moment.",
  observation: "Names a pattern they've quietly noticed.",
  insight:     "Reveals a non-obvious truth.",
};

// Type-specific visual styling for the 4 post types.

const TYPE_META = {
  Educate:   { icon: BookOpen,    hue: "text-sky-300",     bg: "bg-sky-500/10",     ring: "ring-sky-500/40",     accent: "from-sky-500 to-cyan-400",        border: "border-sky-500/40",     hover: "hover:border-sky-400/70",     label: "Educate",   badge: "bg-sky-500/15 text-sky-200 border-sky-400/40" },
  Entertain: { icon: Laugh,       hue: "text-pink-300",    bg: "bg-pink-500/10",    ring: "ring-pink-500/40",    accent: "from-pink-500 to-fuchsia-400",    border: "border-pink-500/40",    hover: "hover:border-pink-400/70",    label: "Entertain", badge: "bg-pink-500/15 text-pink-200 border-pink-400/40" },
  Inspire:   { icon: Flame,       hue: "text-amber-300",   bg: "bg-amber-500/10",   ring: "ring-amber-500/40",   accent: "from-amber-500 to-orange-400",    border: "border-amber-500/40",   hover: "hover:border-amber-400/70",   label: "Inspire",   badge: "bg-amber-500/15 text-amber-200 border-amber-400/40" },
  Sell:      { icon: ShoppingBag, hue: "text-emerald-300", bg: "bg-emerald-500/10", ring: "ring-emerald-500/40", accent: "from-emerald-500 to-teal-400",    border: "border-emerald-500/40", hover: "hover:border-emerald-400/70", label: "Sell",      badge: "bg-emerald-500/15 text-emerald-200 border-emerald-400/40" },
};

const Block = ({ label, copy, children, editable = true }) => (
  <div>
    <div className="flex items-center justify-between mb-1.5">
      <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-zinc-500 inline-flex items-center gap-1.5">
        {label}
        {editable && <Pencil className="h-2.5 w-2.5 text-zinc-700" strokeWidth={2} />}
      </span>
      {copy != null && <CopyButton text={copy} size="xs" label="Copy" />}
    </div>
    {children}
  </div>
);

// "Why this hook?" tooltip — a tiny inline pill that on hover surfaces the
// trend signal anchoring this specific post. Pulls from the post's
// anchor_trend / anchor_source / hook_style / trending_signals_used so the
// user can see, per post, exactly which Reddit thread / search query /
// YouTube autocomplete the hook is grounded in.
const WhyThisHookTooltip = ({ post, index }) => {
  const sourceMeta = ANCHOR_SOURCE_META[post.anchor_source] || null;
  const styleNote = post.hook_style ? HOOK_STYLE_NOTE[post.hook_style] : null;
  const signals = Array.isArray(post.trending_signals_used)
    ? post.trending_signals_used.slice(0, 3)
    : [];

  return (
    <TooltipProvider delayDuration={120}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            data-testid={`why-this-hook-${index}`}
            aria-label="Why this hook? Show the trend signal anchoring this post"
            className="inline-flex items-center gap-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 hover:bg-emerald-500/20 hover:border-emerald-400/70 px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.18em] text-emerald-200 cursor-help transition-colors"
          >
            <HelpCircle className="h-2.5 w-2.5" strokeWidth={2.6} />
            Why this hook?
          </button>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          align="start"
          sideOffset={8}
          className="max-w-xs bg-zinc-950 border border-emerald-500/30 text-zinc-200 p-3.5 shadow-[0_20px_60px_-15px_rgba(16,185,129,0.45)]"
        >
          <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-emerald-300 mb-2">
            Trend grounding
          </div>

          <div className="space-y-2 text-[12.5px] leading-relaxed">
            {sourceMeta && (
              <div className="flex items-start gap-2">
                <Radar className="h-3 w-3 mt-1 text-emerald-300 shrink-0" strokeWidth={2.2} />
                <div>
                  <span className="text-emerald-200 font-semibold">
                    {sourceMeta.label}
                  </span>
                  <span className="text-zinc-400"> · {sourceMeta.note}</span>
                </div>
              </div>
            )}

            {post.anchor_trend && (
              <div>
                <div className="text-zinc-400 text-[10px] uppercase tracking-wider font-semibold mb-0.5">
                  Anchor signal
                </div>
                <div className="text-zinc-100 italic">"{post.anchor_trend}"</div>
              </div>
            )}

            {post.hook_style && styleNote && (
              <div>
                <div className="text-zinc-400 text-[10px] uppercase tracking-wider font-semibold mb-0.5">
                  Hook style · {post.hook_style}
                </div>
                <div className="text-zinc-300">{styleNote}</div>
              </div>
            )}

            {signals.length > 0 && (
              <div className="pt-1.5 border-t border-white/10">
                <div className="text-zinc-400 text-[10px] uppercase tracking-wider font-semibold mb-1">
                  Live signals used
                </div>
                <ul className="space-y-0.5 text-[11.5px] text-zinc-300">
                  {signals.map((s, i) => (
                    <li key={i} className="flex gap-1.5">
                      <span className="text-emerald-400 shrink-0">•</span>
                      <span className="truncate">{s}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export const PostCard = ({ post, index = 0, onChange, apiKey }) => {
  const meta = TYPE_META[post.type] || TYPE_META.Educate;
  const Icon = meta.icon;

  // User-edit tracking. The `_edited` map records which fields the user has
  // manually touched. The Improve flow excludes these from regeneration unless
  // the user explicitly opts in via the dialog's "Allow improvement of my
  // edits" toggle.
  const editedFields = post?._edited || {}; // e.g. { hook: true, "scenes[1]": true }

  // Improve-dialog state.
  const [improveOpen, setImproveOpen] = useState(false);

  // Mark a top-level field as user-edited the moment they change it.
  const updateAndMark = (field, value) => {
    const nextEdited = { ...editedFields, [field]: true };
    onChange && onChange({ ...post, [field]: value, _edited: nextEdited });
  };

  const lockedFields = Object.keys(editedFields).filter((k) => editedFields[k]);

  const openImprovePost = () => setImproveOpen(true);

  const handleImproveApply = (result) => {
    if (!result || result.kind !== "post") return;
    const patch = result.patch || {};
    onChange && onChange({ ...post, ...patch });
  };

  const hashtagsString = (post.hashtags || []).join(" ");
  const onHashtagsChange = (str) => {
    const tags = str.split(/[\s,]+/).map((t) => t.trim()).filter(Boolean)
      .map((t) => (t.startsWith("#") ? t : `#${t}`));
    updateAndMark("hashtags", tags);
  };

  const scenesText = (post.scenes || [])
    .map((s, i) => {
      // v17 shape: role / script / on_screen_text / image_prompt / pexels_query / duration.
      // Back-compat for older shapes (sentence / description / action).
      const role = typeof s.role === "string" ? s.role.toUpperCase() : "";
      const spoken = s.script || s.sentence || s.description || s.action || "";
      const onScreen = s.on_screen_text || "";
      const imagePrompt = s.image_prompt || "";
      const query = s.pexels_query || s.pexels_keywords || s.visual_prompt || "";
      const duration = s.duration != null ? `${s.duration}s` : (s.label || "");
      const header = `Scene ${i + 1}${role ? ` (${role})` : ""}${duration ? ` · ${duration}` : ""}`;
      const parts = [
        header,
        spoken && `Script: ${spoken}`,
        onScreen && `On-screen: ${onScreen}`,
        imagePrompt && `Image prompt: ${imagePrompt}`,
        query && `Pexels: ${query}`,
      ].filter(Boolean);
      return parts.join(" · ");
    })
    .join("\n");

  const fullText = [
    `[${post.type}]`,
    post.anchor_trend ? `ANCHOR TREND: ${post.anchor_trend}` : "",
    post.trending_signals_used?.length
      ? `TRENDING SIGNALS USED:\n- ${post.trending_signals_used.join("\n- ")}`
      : "",
    post.niche_outcome ? `NICHE OUTCOME: ${post.niche_outcome}` : (post.insight_concept ? `INSIGHT CONCEPT: ${post.insight_concept}` : ""),
    `HOOK:\n${post.hook}`,
    `SCRIPT:\n${post.script}`,
    `SCENES:\n${scenesText}`,
    `CTA:\n${post.cta}`,
    `CAPTION:\n${post.caption}`,
    post.hashtags?.length ? `HASHTAGS:\n${post.hashtags.join(" ")}` : "",
    post.trending_audio_suggestion?.song
      ? `TRENDING AUDIO:\n${post.trending_audio_suggestion.song}${post.trending_audio_suggestion.artist ? ` — ${post.trending_audio_suggestion.artist}` : ""}${post.trending_audio_suggestion.vibe ? ` (${post.trending_audio_suggestion.vibe})` : ""}${post.trending_audio_suggestion.why ? `\n${post.trending_audio_suggestion.why}` : ""}`
      : "",
  ].filter(Boolean).join("\n\n");

  return (
    <div
      className={`relative rounded-2xl border ${meta.border} ${meta.hover} bg-zinc-900/70 p-6 pt-7 fade-up transition-all duration-300 hover:-translate-y-0.5 overflow-hidden`}
      style={{ animationDelay: `${index * 80}ms` }}
      data-testid={`post-card-${post.type?.toLowerCase()}`}
    >
      {/* Type accent bar at the top */}
      <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${meta.accent}`} />

      <div className="flex items-start justify-between gap-3 mb-5">
        <div className="flex items-center gap-3">
          <div className={`inline-flex h-11 w-11 items-center justify-center rounded-xl ${meta.bg} ${meta.hue} ring-1 ${meta.ring}`}>
            <Icon className="h-5 w-5" strokeWidth={2} />
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-500">
              Post · {String(index + 1).padStart(2, "0")}
            </div>
            <div className="flex items-center gap-2 -mt-0.5">
              <div className="font-display text-lg font-bold text-white">{post.type}</div>
              <span className={`text-[9px] font-bold uppercase tracking-[0.2em] border rounded-full px-2 py-0.5 ${meta.badge}`}>
                {meta.label}
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            onClick={openImprovePost}
            disabled={!apiKey}
            data-testid={`improve-post-${index}`}
            className="h-8 rounded-full bg-violet-500/15 hover:bg-violet-500/25 border border-violet-500/40 text-violet-200 text-[11px] font-semibold px-3 disabled:opacity-50"
            title="Tell the AI what to improve about this post"
          >
            <Wand2 className="h-3 w-3 mr-1.5" strokeWidth={2.5} />
            Improve
          </Button>
          <CopyButton text={fullText} label="Copy all" size="xs" />
        </div>
      </div>

      {/* Anchor trend — the ONE signal this entire post is built around */}
      {post.anchor_trend && (
        <div className="mb-3" data-testid={`anchor-trend-${index}`}>
          <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-zinc-500 mb-1.5">
            Anchor trend
          </div>
          <div className="inline-flex items-start gap-2 rounded-lg bg-fuchsia-500/10 ring-1 ring-fuchsia-500/30 px-3 py-2 max-w-full">
            <Radar className="h-3.5 w-3.5 text-fuchsia-300 mt-0.5 shrink-0" strokeWidth={1.8} />
            <span className="text-[12.5px] font-medium text-fuchsia-100 leading-snug">
              {post.anchor_trend}
            </span>
          </div>
        </div>
      )}

      {/* Trending signals used — auto-populated by the AI from live Reddit/Google/YouTube data */}
      {Array.isArray(post.trending_signals_used) && post.trending_signals_used.length > 0 && (
        <div className="mb-4" data-testid={`trending-signals-${index}`}>
          <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-zinc-500 mb-1.5">
            Trending signals used
          </div>
          <div className="flex flex-wrap gap-1.5">
            {post.trending_signals_used.slice(0, 6).map((sig, si) => (
              <span
                key={si}
                title={sig}
                className="text-[10px] text-amber-200/90 bg-amber-500/10 border border-amber-500/30 rounded-full px-2 py-0.5 max-w-[280px] truncate"
              >
                {sig}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Niche outcome — the opportunity-framed phrase the CTA promises to deliver */}
      {(post.niche_outcome || post.insight_concept) && (
        <div className="mb-3" data-testid={`niche-outcome-${index}`}>
          <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-zinc-500 mb-1.5">
            Niche outcome
          </div>
          <span className="inline-flex text-[11px] text-violet-200 bg-violet-500/10 border border-violet-500/30 rounded-full px-2.5 py-1">
            {post.niche_outcome || post.insight_concept}
          </span>
        </div>
      )}

      {/* Specificity marker — the concrete number / comparison / scenario the script is anchored on */}
      {post.specificity_marker && (
        <div className="mb-4" data-testid={`specificity-marker-${index}`}>
          <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-zinc-500 mb-1.5">
            Specificity
          </div>
          <span className="inline-flex text-[11px] text-emerald-200 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-2.5 py-1">
            {post.specificity_marker}
          </span>
        </div>
      )}

      <div className="space-y-5">
        <Block
          label={
            <span className="inline-flex items-center gap-2">
              Hook
              {typeof post.hook_score === "number" && (
                <span
                  data-testid={`hook-score-${index}`}
                  title={post.hook_score_justification || ""}
                  className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.18em] border ${
                    post.hook_score >= 9
                      ? "border-violet-500/50 bg-violet-500/15 text-violet-200"
                      : post.hook_score >= 8
                      ? "border-emerald-500/50 bg-emerald-500/15 text-emerald-200"
                      : "border-amber-500/50 bg-amber-500/15 text-amber-200"
                  }`}
                >
                  {post.hook_score >= 9 ? "Viral-grade" : post.hook_score >= 8 ? "Strong" : "OK"}
                  · {Number(post.hook_score).toFixed(1)}
                </span>
              )}
              {(post.anchor_trend || post.anchor_source || post.hook_style) && (
                <WhyThisHookTooltip post={post} index={index} />
              )}
            </span>
          }
          copy={post.hook}
        >
          <div className="flex items-start gap-2">
            <Quote className="mt-2 h-3.5 w-3.5 text-amber-400 shrink-0" strokeWidth={1.8} />
            <EditableText
              value={post.hook || ""}
              onChange={(v) => updateAndMark("hook", v)}
              placeholder="Scroll-stopping hook..."
              ariaLabel="Hook"
              className="font-display text-lg font-semibold text-white leading-snug"
            />
          </div>
        </Block>

        <Block label="Script (12–20s · 4 scenes)" copy={post.script}>
          <EditableText
            value={post.script || ""}
            onChange={(v) => updateAndMark("script", v)}
            placeholder="12–20 second spoken script (4 short sentences)..."
            ariaLabel="Script"
            minRows={3}
          />
        </Block>

        {/* Auto-generated scene plan — read-only, no user interaction required */}
        <Block label="Scene Plan (4 scenes · 12s)" copy={scenesText} editable={false}>
          <div className="space-y-2">
            {(post.scenes || []).map((s, i) => {
              // v17 shape: role / script / on_screen_text / image_prompt / pexels_query / duration.
              // Back-compat: older fields are shown if present.
              const role = typeof s.role === "string" ? s.role.toUpperCase() : "";
              const spoken = s.script ?? s.sentence ?? s.description ?? s.action ?? "";
              const onScreen = s.on_screen_text ?? "";
              const imagePrompt = s.image_prompt ?? "";
              const query = s.pexels_query ?? s.pexels_keywords ?? s.visual_prompt ?? "";
              const duration = s.duration != null ? `${s.duration}s` : (s.label ?? "");
              return (
                <div
                  key={i}
                  className="rounded-xl border border-white/10 bg-zinc-950 p-2.5"
                  data-testid={`scene-${index}-${i}`}
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="inline-flex items-center justify-center h-5 min-w-[22px] rounded-full bg-amber-500/10 ring-1 ring-amber-500/30 text-amber-300 text-[10px] font-bold px-1.5">
                      {i + 1}
                    </span>
                    {role && (
                      <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-violet-200 bg-violet-500/10 border border-violet-500/30 rounded-full px-2 py-0.5">
                        {role}
                      </span>
                    )}
                    {duration && (
                      <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-zinc-500">
                        {duration}
                      </span>
                    )}
                  </div>
                  {spoken && (
                    <div className="mt-1.5 text-[13px] text-zinc-200 leading-snug italic">
                      <span className="text-[10px] not-italic font-bold uppercase tracking-[0.18em] text-zinc-600 mr-1.5">Script</span>
                      "{spoken}"
                    </div>
                  )}
                  {onScreen && (
                    <div className="mt-1.5 text-[12px] text-amber-100 leading-snug">
                      <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-600 mr-1.5">On-screen</span>
                      {onScreen}
                    </div>
                  )}
                  {imagePrompt && (
                    <div className="mt-1.5 text-[11px] text-sky-200 leading-snug">
                      <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-600 mr-1.5">Image prompt</span>
                      {imagePrompt}
                    </div>
                  )}
                  {query && (
                    <div className="mt-1.5">
                      <code className="text-[11px] text-emerald-200 bg-emerald-500/10 border border-emerald-500/30 rounded px-1.5 py-0.5 font-mono break-all">
                        {query}
                      </code>
                    </div>
                  )}
                  {s.visual && s.visual !== sentence && (
                    <div className="mt-1.5 text-[12px] text-zinc-400 leading-snug">
                      <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-600 mr-1.5">Visual</span>
                      {s.visual}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Block>

        <Block label="Primary CTA" copy={post.cta}>
          <div className="rounded-lg bg-amber-500/10 ring-1 ring-amber-500/30 text-amber-100 px-3 py-2 flex items-start gap-2">
            <Megaphone className="mt-1 h-3.5 w-3.5 shrink-0" strokeWidth={1.8} />
            <EditableText
              value={post.cta || ""}
              onChange={(v) => updateAndMark("cta", v)}
              placeholder="Primary CTA"
              ariaLabel="Primary CTA"
              className="text-sm font-medium text-amber-100"
            />
          </div>
        </Block>

        <Block label="Caption" copy={post.caption}>
          <EditableText
            value={post.caption || ""}
            onChange={(v) => updateAndMark("caption", v)}
            placeholder="Platform caption..."
            minRows={2}
          />
        </Block>

        <Block label="Hashtags (space or comma)" copy={(post.hashtags || []).join(" ")}>
          <div className="flex items-start gap-2">
            <Hash className="mt-2 h-3.5 w-3.5 text-amber-400 shrink-0" strokeWidth={2} />
            <EditableText
              value={hashtagsString}
              onChange={onHashtagsChange}
              placeholder="#digitalnomad #onlinebusiness ..."
              className="text-sm text-zinc-200"
            />
          </div>
        </Block>

        {/* Trending audio suggestion — 1 track per post */}
        {post.trending_audio_suggestion && (post.trending_audio_suggestion.song || post.trending_audio_suggestion.artist) && (
          <Block
            label="Trending Audio (suggestion)"
            copy={`${post.trending_audio_suggestion.song || ""} — ${post.trending_audio_suggestion.artist || ""}`}
            editable={false}
          >
            <div
              className="rounded-xl border border-pink-500/30 bg-pink-500/5 p-3 flex items-start gap-3"
              data-testid={`audio-suggestion-${index}`}
            >
              <div className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-pink-500/15 text-pink-300 ring-1 ring-pink-500/30">
                <Music2 className="h-4 w-4" strokeWidth={2} />
              </div>
              <div className="min-w-0 flex-1 space-y-0.5">
                <div className="text-sm font-semibold text-white truncate">
                  {post.trending_audio_suggestion.song || "—"}
                </div>
                <div className="text-[12px] text-pink-200/80 truncate">
                  {post.trending_audio_suggestion.artist || ""}
                  {post.trending_audio_suggestion.vibe && (
                    <span className="ml-2 inline-block text-[10px] uppercase tracking-[0.18em] text-pink-300/80 bg-pink-500/10 border border-pink-500/30 rounded-full px-1.5 py-0.5">
                      {post.trending_audio_suggestion.vibe}
                    </span>
                  )}
                </div>
                {post.trending_audio_suggestion.why && (
                  <div className="text-[11.5px] text-zinc-400 leading-snug pt-1">
                    {post.trending_audio_suggestion.why}
                  </div>
                )}
              </div>
            </div>
          </Block>
        )}

      </div>

      {/* User Improvement Mode */}
      <ImproveDialog
        open={improveOpen}
        onOpenChange={setImproveOpen}
        apiKey={apiKey}
        scope="post"
        post={post}
        lockedFields={lockedFields}
        onApply={handleImproveApply}
      />
    </div>
  );
};

export default PostCard;
